<script setup lang="ts">
import { reactive, ref, watch } from 'vue';
import { api } from '../lib/api';
import type { RelationshipCategory, RelationshipPreset } from '../types/relationships';

const props = defineProps<{ workspaceId: string }>();
const categories: Array<{ id: RelationshipCategory; label: string }> = [
  { id: 'family', label: 'Familie' }, { id: 'romantic', label: 'Partnerschaft' },
  { id: 'social', label: 'Freunde & Soziales' }, { id: 'professional', label: 'Beruflich' },
  { id: 'conflict', label: 'Konflikte' }, { id: 'other', label: 'Weitere' }
];
const items = ref<RelationshipPreset[]>([]);
const loading = ref(true);
const busy = ref(false);
const error = ref('');
const editingId = ref<string | null>(null);
const formOpen = ref(false);
const draft = reactive<RelationshipPreset>({ id: '', category: 'other', label: '', inverseLabel: '', status: '' });

async function load() {
  if (!props.workspaceId) return;
  loading.value = true; error.value = '';
  try { items.value = (await api<{ items: RelationshipPreset[] }>(`/api/v1/workspaces/${props.workspaceId}/relationship-presets`)).items; }
  catch (cause) { error.value = cause instanceof Error ? cause.message : 'Beziehungsvorgaben konnten nicht geladen werden.'; }
  finally { loading.value = false; }
}
watch(() => props.workspaceId, () => { void load(); }, { immediate: true });

function newPreset() {
  editingId.value = null;
  Object.assign(draft, { id: '', category: 'other', label: '', inverseLabel: '', status: '' });
  formOpen.value = true; error.value = '';
}
function editPreset(item: RelationshipPreset) {
  editingId.value = item.id;
  Object.assign(draft, item);
  formOpen.value = true; error.value = '';
}
async function persist(next: RelationshipPreset[]) {
  busy.value = true; error.value = '';
  try {
    items.value = (await api<{ items: RelationshipPreset[] }>(`/api/v1/workspaces/${props.workspaceId}/relationship-presets`, {
      method: 'PUT', body: JSON.stringify({ items: next })
    })).items;
    formOpen.value = false;
    editingId.value = null;
  } catch (cause) { error.value = cause instanceof Error ? cause.message : 'Beziehungsvorgaben konnten nicht gespeichert werden.'; }
  finally { busy.value = false; }
}
async function savePreset() {
  const item = { id: draft.id.trim().toLowerCase(), category: draft.category, label: draft.label.trim(), inverseLabel: draft.inverseLabel.trim(), status: draft.status.trim() };
  if (!item.id || !item.label || !item.inverseLabel || !item.status) { error.value = 'Bitte alle Felder ausfüllen.'; return; }
  if (item.id === 'custom' || items.value.some((existing) => existing.id === item.id && existing.id !== editingId.value)) { error.value = 'Der interne Typ ist bereits vergeben oder reserviert.'; return; }
  const next = editingId.value ? items.value.map((existing) => existing.id === editingId.value ? item : existing) : [...items.value, item];
  await persist(next);
}
async function removePreset(item: RelationshipPreset) {
  if (!window.confirm(`Beziehungsvorgabe „${item.label}“ entfernen? Bereits gespeicherte Beziehungen bleiben erhalten.`)) return;
  await persist(items.value.filter((existing) => existing.id !== item.id));
}
async function movePreset(index: number, direction: -1 | 1) {
  const next = [...items.value];
  const neighbor = index + direction;
  if (neighbor < 0 || neighbor >= next.length) return;
  [next[index], next[neighbor]] = [next[neighbor]!, next[index]!];
  await persist(next);
}
</script>

<template>
  <section class="preset-settings">
    <header><div><h2>Standard-Beziehungen</h2><p>Diese Vorgaben erscheinen im Beziehungseditor. Änderungen gelten für neue Beziehungen; bestehende Einträge behalten ihre Bezeichnungen.</p></div><button type="button" :disabled="busy || loading" @click="newPreset">＋ Beziehungsvorgabe</button></header>
    <p v-if="error" class="preset-error" role="alert">{{ error }}</p>
    <p v-if="loading">Beziehungsvorgaben werden geladen …</p>
    <div v-else class="preset-list">
      <article v-for="(item, index) in items" :key="item.id">
        <div><strong>{{ item.label }} ↔ {{ item.inverseLabel }}</strong><small>{{ categories.find((category) => category.id === item.category)?.label }} · {{ item.status }} · {{ item.id }}</small></div>
        <div class="preset-actions"><button type="button" :disabled="busy || index === 0" title="Nach oben" @click="movePreset(index, -1)">↑</button><button type="button" :disabled="busy || index === items.length - 1" title="Nach unten" @click="movePreset(index, 1)">↓</button><button type="button" :disabled="busy" @click="editPreset(item)">Bearbeiten</button><button type="button" class="danger" :disabled="busy" @click="removePreset(item)">Löschen</button></div>
      </article>
      <p v-if="!items.length">Noch keine Vorgaben. Eigene Bezeichnungen bleiben im Editor verfügbar.</p>
    </div>
    <form v-if="formOpen" class="preset-form" @submit.prevent="savePreset">
      <h3>{{ editingId ? 'Vorgabe bearbeiten' : 'Neue Vorgabe' }}</h3>
      <div class="preset-fields"><label>Interner Typ<input v-model="draft.id" pattern="[a-z][a-z0-9-]*" maxlength="80" required :disabled="Boolean(editingId)" placeholder="z. B. commander" /></label><label>Kategorie<select v-model="draft.category"><option v-for="category in categories" :key="category.id" :value="category.id">{{ category.label }}</option></select></label><label>Bezeichnung<input v-model="draft.label" maxlength="160" required placeholder="z. B. Befehligt" /></label><label>Gegenrichtung<input v-model="draft.inverseLabel" maxlength="160" required placeholder="z. B. Untersteht" /></label><label>Zustand<input v-model="draft.status" maxlength="240" required placeholder="z. B. Kommando aktiv" /></label></div>
      <footer><button type="button" :disabled="busy" @click="formOpen = false">Abbrechen</button><button type="submit" :disabled="busy">{{ busy ? 'Speichert …' : 'Vorgabe speichern' }}</button></footer>
    </form>
  </section>
</template>

<style scoped>
.preset-settings { display:grid; gap:18px; max-width:980px; margin:0 auto; }
.preset-settings>header { display:flex; align-items:start; justify-content:space-between; gap:18px; }
.preset-settings h2,.preset-settings h3 { margin:0; }
.preset-settings p,.preset-list small { color:var(--ui-text-muted); }
.preset-settings header p { max-width:690px; margin:7px 0 0; }
.preset-settings button { border:1px solid var(--ui-border); border-radius:7px; background:var(--ui-surface-3); color:var(--ui-text); padding:7px 10px; cursor:pointer; }
.preset-settings button:disabled { opacity:.5; cursor:default; }
.preset-list { display:grid; gap:8px; }
.preset-list article { display:flex; align-items:center; justify-content:space-between; gap:12px; border:1px solid var(--ui-border); border-radius:9px; background:var(--ui-surface-2); padding:12px; }
.preset-list article>div:first-child { display:grid; gap:4px; }
.preset-actions { display:flex; gap:5px; }
.preset-actions .danger,.preset-error { color:var(--ui-danger-text)!important; }
.preset-form { display:grid; gap:14px; border:1px solid var(--ui-border); border-radius:10px; background:var(--ui-surface-2); padding:16px; }
.preset-fields { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:12px; }
.preset-fields label { display:grid; gap:5px; color:var(--ui-text-muted); font-size:11px; font-weight:700; }
.preset-fields input,.preset-fields select { width:100%; box-sizing:border-box; border:1px solid var(--ui-border); border-radius:6px; background:var(--ui-surface-1); color:var(--ui-text); padding:8px; }
.preset-form footer { display:flex; justify-content:end; gap:8px; }
@media(max-width:700px) {
  .preset-settings>header,.preset-list article { align-items:stretch; flex-direction:column; }
  .preset-fields { grid-template-columns:1fr; }
  .preset-actions { flex-wrap:wrap; }
}
</style>
